<template>
    <div class="vx-relative vx-mb-4 vx-flex vx-flex-col vx-gap-2">
        <vx-box
            class="vx-relative vx-flex vx-p-4 vx-gap-2 vx-items-start vx-flex-wrap lg:vx-flex-nowrap lg:vx-py-3 lg:vx-pl-6 lg:vx-pr-3">
            <div class="vx-w-[100px] vx-shrink-0 vx-order-1 vx-pt-2">
                <font-awesome-icon :icon="faShareNodes"
                    class="vx-mr-2 vx-text-sky-500"></font-awesome-icon>
                <span class="vx-whitespace-nowrap vx-text-slate-800 vx-text-base vx-font-extrabold">
                    {{ props.filterIndex === 0 ? 'If' : 'And If' }}
                </span>
            </div>
            <div class="vx-grow vx-w-full vx-flex vx-flex-col vx-gap-2 vx-order-2 lg:vx-order-1 lg:vx-grow-0">
                <div data-test="attribute-name-wrapper"
                    :class="customAttributeKeyAndValue() ? 'vx-col-span-12' : 'vx-col-span-7'">
                    <vx-selectable placeholder="attribute name"
                        v-model="filter.attribute"
                        :options="[
                            {
                                value: 'custom_attribute',
                                label: 'group has a custom attribute with key and value',
                            },
                            {
                                value: 'group_id',
                                label: 'group',
                            },
                        ]"
                        :reduce="option => option.value"
                        :clearable="false"
                        :error="displayFirstError(validate.attribute)">

                    </vx-selectable>
                </div>

                <div v-if="!customAttributeKeyAndValue() && filter.attribute">
                    <vx-selectable placeholder="operator"
                        data-test="operator-select"
                        v-model="filter.operator"
                        :reduce="option => option.value"
                        :options="operatorOptions"
                        :clearable="false"
                        :error="displayFirstError(validate.operator)">
                    </vx-selectable>
                </div>

                <div v-if="!customAttributeKeyAndValue() && filter.attribute">
                    <group-collection-filter-values v-model="filter.values"
                        :groupSelect="filter.attribute === 'group_id'"></group-collection-filter-values>
                </div>

            </div>
            <div class="vx-flex vx-ml-auto vx-order-1 lg:vx-order-2">
                <vx-button @click.prevent="emit('remove')"
                    type="button"
                    size="md"
                    color="muted-light"
                    data-test="remove-filter">
                    <font-awesome-icon :icon="faTrash"></font-awesome-icon>
                </vx-button>
            </div>
        </vx-box>

        <vx-box data-test="custom-attribute-key-and-value-wrapper"
            class="vx-relative vx-p-4 vx-ml-10 lg:vx-ml-[122px] lg:vx-p-6"
            v-if="customAttributeKeyAndValue()">
            <line-nested></line-nested>

            <div class="vx-font-extrabold vx-text-slate-800 vx-mb-4">Custom Attribute</div>
            <div class="vx-flex vx-flex-col vx-gap-2">
                <div>
                    <vx-input data-test="custom-attribute-key"
                        v-model="filter.key"
                        placeholder="custom attribute key"
                        :error="displayFirstError(validate.key)"></vx-input>
                </div>

                <div>
                    <vx-selectable placeholder="operator"
                        data-test="operator-select"
                        v-model="filter.operator"
                        :reduce="option => option.value"
                        :options="operatorOptions"
                        :clearable="false"
                        :error="displayFirstError(validate.operator)">
                    </vx-selectable>
                </div>

                <div>
                    <group-collection-filter-values v-model="filter.values"></group-collection-filter-values>
                </div>

            </div>
        </vx-box>
    </div>

</template>


<script setup>
import { faShareNodes, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton, VxSelectable, VxInput } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import { h, watch } from 'vue';
import { csvInjection, displayFirstError, groupCustomAttributeKey } from '~/utils/validation';
import GroupCollectionFilterValues from './GroupCollectionFilterValues.vue';

const LineNested = h('div', { class: 'vx-absolute vx-top-9 -vx-left-6 vx-w-6 vx-h-px vx-border-solid vx-border-y vx-border-x-0 vx-border-t-slate-300 vx-border-b-white lg:-vx-left-[72px] lg:vx-w-[72px]' })

const operatorOptions = [
    {
        value: 'in',
        label: 'is in list',
    },
    {
        value: 'not_in',
        label: 'is not in list',
    },
]

const props = defineProps({
    filterIndex: {
        type: Number,
        required: true,
    }
});

const filter = defineModel({
    type: Object,
    required: true,
});

watch(() => filter.value.attribute, () => {
    filter.value.key = '';
})

const emit = defineEmits(['remove']);

const customAttributeKeyAndValue = () => {
    return filter.value.attribute === 'custom_attribute';
}

const validate = useVuelidate({
    attribute: {
        required: helpers.withMessage('Attribute is required', required),
    },
    key: {
        requiredIf: helpers.withMessage('Key is required', requiredIf(() => customAttributeKeyAndValue())),
        maxLength: maxLength(40),
        format: groupCustomAttributeKey,
        csvInjection: csvInjection,
    },
    operator: {
        required: helpers.withMessage('Operator is required', required),
    },
}, filter, {
    $autoDirty: true,
});


</script>
